import React from 'react';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { ColorPalette, TypographySettings, Dimensions } from '../config';

interface ICardStyleParams {
  contentBackgroundColor: string;
}

const useStyles = makeStyles<ICardStyleParams, 'dense' | 'cardTitle' | 'noHorizontalPadding'>()(
  (_theme, params, classes) => ({
    cardRoot: {
      boxShadow: '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
    },
    cardRootFlex: {
      display: 'inline-flex',
      flexDirection: 'column',
    },
    cardTitle: {
      backgroundColor: ColorPalette.LightHeaderBackground,
      borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
      borderRadius: '4px 4px 0 0',
      color: ColorPalette.DefaultTextColor,
      fontFamily: TypographySettings.AlternativeFontFamily,
      fontSize: TypographySettings.RootFontSize,
      fontWeight: TypographySettings.AlternativeFontSemiBold,
      minHeight: 42,
      [`&.${classes.dense}`]: {
        minHeight: 0,
      },
    },
    cardGutter: {
      padding: Dimensions.ThemeSpacingUnit * 1,
      paddingLeft: Dimensions.ThemeSpacingUnit * 2,
      paddingRight: Dimensions.ThemeSpacingUnit * 2,
      '@media (min-width: 600px)': {
        paddingLeft: Dimensions.ThemeSpacingUnit * 2,
        paddingRight: Dimensions.ThemeSpacingUnit * 2,
      },
      [`&.${classes.dense}`]: {
        paddingTop: Dimensions.ThemeSpacingUnit * 0.5,
        paddingRight: Dimensions.ThemeSpacingUnit * 1.5,
        paddingBottom: Dimensions.ThemeSpacingUnit * 0.5,
        paddingLeft: Dimensions.ThemeSpacingUnit * 1.5,
      },
      [`&.${classes.cardTitle}`]: {
        paddingTop: 12,
        [`&.${classes.dense}`]: {
          paddingTop: Dimensions.ThemeSpacingUnit,
        },
      },
      [`&.${classes.noHorizontalPadding}`]: {
        paddingLeft: 0,
        paddingRight: 0,
      },
    },
    content: {
      borderRadius: '0 0 4px 4px',
    },
    contentBackgroundColor: {
      backgroundColor: params.contentBackgroundColor ?? 'transparent',
    },
    dense: {
      fontSize: 12,
    },
    noHorizontalPadding: {},
    fullHeight: {
      height: '100%',
    },
  })
);

export function CardComponent(
  props: React.PropsWithChildren<{
    title?: string;
    autoWidth?: boolean;
    fullHeight?: boolean;
    contentBackgroundColor?: string;
    dense?: boolean;
    className?: string;
    noHorizontalPadding?: boolean;
    'data-vanguard'?: string;
  }>
) {
  const {
    title,
    children,
    autoWidth = false,
    fullHeight = false,
    contentBackgroundColor = 'transparent',
    dense = false,
    className = '',
    noHorizontalPadding = false,
    'data-vanguard': dataVanguard,
  } = props;
  const { classes } = useStyles({ contentBackgroundColor });

  return (
    <div
      className={clsx(classes.cardRoot, { [classes.cardRootFlex]: autoWidth, [classes.dense]: dense, [classes.fullHeight]: fullHeight, [className]: !!className })}
      data-vanguard={dataVanguard}
    >
      {title && <div className={clsx(classes.cardTitle, classes.cardGutter, { [classes.dense]: dense })}>{title}</div>}
      <div
        className={clsx(classes.cardGutter, classes.contentBackgroundColor, classes.content, {
          [classes.dense]: dense,
          [classes.noHorizontalPadding]: noHorizontalPadding,
          [classes.fullHeight]: fullHeight,
        })}
      >
        {children}
      </div>
    </div>
  );
}
